import React from 'react';

import { useCustomContentTypeSkeletonCount } from '@lumapps/content-types/hooks/useCustomContentTypeSkeletonCount';
import { CustomContentType } from '@lumapps/content-types/types';
import { Menu } from '@lumapps/lumx-menu/components/Menu';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { SpaceContentTypeSubmenu } from './SpaceContentTypeSubmenu';

export interface SpaceContentContributionMenuProps {
    /** Id of the space */
    spaceId: string;
    /** Determines whether we are currently fetching the list or not */
    isLoadingCustomContentTypes?: boolean;
    /** the list of custom content types to display */
    parentCustomContentTypes?: CustomContentType[];
    /** True if the current user is super admin */
    isSuperAdmin?: boolean;
    /** Key/value object where the key is the id of the custom content type and the value is true/false */
    allowedCustomContentTypes: Record<string, boolean>;
    /** Key/value object where the key is the id of the custom content type and the value tells if the user can design this content type */
    canDesignAuthorizations: Record<string, boolean>;
    /** Whether the new contribution experience is enabled or not */
    isNewContributionExperienceEnabled?: boolean;
    /** Whether the new writer editor is enabled */
    isNewWriterEditorEnabled?: boolean;
}

/**
 * Menu of all content types available to create content into from a space.
 */
export const SpaceContentContributionMenu: React.FC<SpaceContentContributionMenuProps> = (props) => {
    const {
        spaceId,
        isLoadingCustomContentTypes = false,
        parentCustomContentTypes,
        isSuperAdmin = false,
        allowedCustomContentTypes = {},
        canDesignAuthorizations,
        isNewContributionExperienceEnabled,
        isNewWriterEditorEnabled,
    } = props;
    const { translateKey } = useTranslate();

    const skeletonCount = useCustomContentTypeSkeletonCount();

    // Check user is super admin or has access to the custom content type.
    const customContentTypes = React.useMemo(
        () =>
            isSuperAdmin
                ? parentCustomContentTypes
                : parentCustomContentTypes?.filter((cct) => allowedCustomContentTypes[cct.id]),
        [isSuperAdmin, parentCustomContentTypes, allowedCustomContentTypes],
    );

    if (isLoadingCustomContentTypes || !customContentTypes) {
        return <Menu.ItemsSkeleton hasIcon itemCount={skeletonCount} loadingMessage={translateKey(GLOBAL.LOADING)} />;
    }

    if (!customContentTypes.length) {
        return null;
    }

    return (
        <>
            {customContentTypes.map((contentType) => (
                <SpaceContentTypeSubmenu
                    key={contentType.id}
                    spaceId={spaceId}
                    contentType={contentType}
                    isSuperAdmin={isSuperAdmin}
                    canDesignAuthorizations={canDesignAuthorizations}
                    isNewContributionExperienceEnabled={isNewContributionExperienceEnabled}
                    isNewWriterEditorEnabled={isNewWriterEditorEnabled}
                />
            ))}
        </>
    );
};
